// NOTE: this component expects it's parent to pass an ApolloProvider client
// see ./index.tsx for an example

import { LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Form, Input } from 'antd'
import { fullNameValidator } from 'dash/shared'
import * as React from 'react'
import { FC, useState } from 'react'

import FormAlert, { FormAlertProps } from 'auth/form_alert'

const FORM_VALIDATE_MSGS = {
  required: '${name} is required',
  types: {
    email: 'please enter a valid email',
  },
}

interface DomainValidationResponse {
  status: 404 | 200
  /**
   * Whether the email provider is free / public.
   */
  free?: boolean
  /**
   * If the provided domain is similar to a known mail domain, this field will populate with a suggested known domain.
   */
  didYouMean?: string
}

type RegistrationFormProps = {
  generateSubmitPayload: (values: any) => { [key: string]: string }
  hideEmail?: boolean
  mutation: any
  onSuccess: (successPayload: any, handleErrors: (errors: any) => void) => void
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
  generateSubmitPayload,
  hideEmail = false,
  mutation: MUTATION,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [formAlert, setFormAlert] = useState<FormAlertProps>(null)
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)

  const handleSignupErrors = (error: any) => {
    let msg: string
    if (Array.isArray(error) && typeof error[0] == 'string') msg = error[0]
    else if (typeof error == 'string') msg = error
    else msg = 'There was an error creating your account.'

    setFormAlert({ msg, type: 'error' })
  }

  const onSignupSuccess = (data: any) => {
    onSuccess(data, handleSignupErrors)
  }

  const [register] = useMutation(MUTATION, {
    onCompleted: onSignupSuccess,
    onError: handleSignupErrors,
  })

  const validateEmailDomain = async (_rule, value) => {
    const invalidDomainErrMsg = 'invalid email domain'
    if (!value) return // don't try to validate email domain for an empty value

    const valueSplit = value.split('@')
    const domain = valueSplit[1]
    if (!domain) return // don't try to validate email domain if there is none present

    try {
      const res = await fetch(`https://fn.powcloud.com/mx/${domain}`, {
        method: 'GET',
      })

      const { status }: DomainValidationResponse = await res.json()

      if (res.status === 200 && status === 200) return // domain is valid

      throw new Error(invalidDomainErrMsg) // domain is invalid
    } catch (err) {
      if (err.message == invalidDomainErrMsg) throw err // domain is invalid

      console.error('Could not fetch email domain validity', err)
      return // be optimistic - assume valid if the actual request failed
    }
  }

  const onSubmit = (values: { name: string; email?: string; password: string }) => {
    register({ variables: generateSubmitPayload(values) })
  }

  // const onSubmitFailed = (errorInfo) => {
  //   console.log('Failed:', errorInfo);
  // };

  const onFieldsChange = () => {
    if (formAlert) setFormAlert(null) // reset form error on any input change
  }

  const validateTrigger = formSubmitted ? 'onChange' : 'onSubmit'

  return (
    <Form
      form={form}
      name="registration"
      // layout="vertical"
      size="middle"
      style={{ minWidth: 300 }}
      initialValues={{ remember: true }}
      onFieldsChange={onFieldsChange}
      onFinish={onSubmit}
      // onFinishFailed={onSubmitFailed} // FE form validation fails
      validateMessages={FORM_VALIDATE_MSGS}
    >
      <FormAlert {...formAlert} />
      <Form.Item
        name="name"
        rules={[
          {
            required: true,
            message: 'Full name is required',
            validator: fullNameValidator,
          },
        ]}
        validateTrigger={validateTrigger}
      >
        <Input size="large" prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Full Name" />
      </Form.Item>
      {!hideEmail && (
        <Form.Item
          name="email"
          rules={[{ required: true, type: 'email' }, { validator: validateEmailDomain }]}
          validateFirst
          validateTrigger={validateTrigger}
        >
          <Input
            size="large"
            prefix={<MailOutlined className="site-form-item-icon" />}
            placeholder="Email"
            // placeholder="user@powcloud.com"
          />
        </Form.Item>
      )}
      <Form.Item
        // label="Password"
        name="password"
        rules={[{ required: true }, { min: 6, message: 'Minimum 6 chars' }]}
        validateTrigger={validateTrigger}
      >
        <Input
          size="large"
          prefix={<LockOutlined className="site-form-item-icon" />}
          type="password"
          placeholder="Password (6 characters minimum)"
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" onClick={() => setFormSubmitted(true)} block>
          Sign up
        </Button>
      </Form.Item>
    </Form>
  )
}
